/**
 * Payer-name comparison — "is this the same insurer, written differently?"
 *
 * Cross-cutting pure helper shared by the backend (the SMS card-recovery
 * different-insurer rule, #267) and the frontend (the recovery drawer's
 * "Suggested by VitalRev AI" marker on a substituted payer), so the badge staff
 * see and the decision the sweep makes can never drift apart.
 *
 * Deliberately biased toward "same": a false difference re-routes a patient's
 * eligibility check — and their PHI — to an insurer that shouldn't receive it,
 * while a false match only leaves us where we already were.
 */

/**
 * Minimum confidence before a card's payer may override the payer on file
 * (#267). 0.8 is the threshold the card-extraction consensus schema itself
 * names for payer: 4 of the 5 independent reads saw the same insurer. A payer
 * the patient TYPED carries 1 (source confidence — their explicit statement)
 * and passes by design; the LLM equivalence gate still confirms it names a
 * genuinely different insurer. Below the threshold, the re-run behaves exactly
 * as it did before and the drawer shows an ordinary read — which is why this
 * number lives HERE rather than once per side: the badge staff see and the
 * substitution the sweep makes must never disagree about what "confident
 * enough" means.
 */
export const PAYER_SUBSTITUTION_MIN_CONFIDENCE = 0.8

/** Case- and punctuation-insensitive squash: "Regence BCBS of Oregon" → "regencebcbsoforegon". */
export function normalizePayerName(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * True when two payer names denote the same insurer as far as we can tell.
 *
 * Equal after squashing, or one contained in the other — "Aetna" vs "Aetna Inc",
 * "Regence" vs "Regence BCBS of Oregon", "Moda" vs "Moda Health". An empty side
 * means we have nothing to compare, which is reported as "same" so a missing
 * value can never masquerade as a difference.
 */
export function samePayerName(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const x = normalizePayerName(a)
  const y = normalizePayerName(b)
  if (!x || !y) return true
  return x === y || x.includes(y) || y.includes(x)
}

/**
 * Resolve a stored #267 verdict (`extraction.payerDiffers`) into "may the payer
 * swap arm?". The stored value is three-state by design:
 *   - true / false — a REAL verdict: the LLM equivalence gate answered, or the
 *     fold reused a decided answer for the unchanged card payer. Trusted as-is.
 *   - null — UNCONFIRMED: the gate was unavailable when the fold ran. Never a
 *     swap — the next fold re-asks. (Treating null as "different" would swap on
 *     a guess; letting it fall through to the fallback would swap on the
 *     ungated string comparison — both wrong.)
 *   - undefined (field absent) — a pre-gate doc: fall back to the caller's
 *     string comparison, the pre-#267 behaviour.
 * Shared by the re-run builder and the drawer badge so the substitution made
 * and the substitution shown can never disagree.
 */
export function resolvePayerDiffers(
  stored: boolean | null | undefined,
  preGateFallback: () => boolean,
): boolean {
  if (stored === undefined) return preGateFallback()
  return stored === true
}
